import type { PositionKind } from '../../domain/types'
import { clamp } from './statistics'
import { decimalToImplied } from './odds'
import { decideAction, expectedValue, type EvDecision } from './ev'

export interface OpportunityInput {
  id: string
  kind: PositionKind
  label: string
  modelProb: number
  decimalOdds: number
  confidence: number
  dataQuality: number
  liquidity: number
  correlationGroup?: string
  feePct?: number
  spreadPct?: number
  notes?: string
}

export interface Explainability {
  dataUsed: string[]
  assumptions: string[]
  whatCouldGoWrong: string[]
  whyModelMayBeWrong: string
  whatInvalidates: string
  verdict: 'trade-worthy' | 'watchlist-only' | 'skip'
}

export interface NoTradeDecision {
  skip: boolean
  reasons: string[]
}

export interface ScoredOpportunity {
  input: OpportunityInput
  marketProb: number
  edge: number
  evPerUnit: number
  score: number
  action: EvDecision
  riskyDespiteEdge: boolean
  noTrade: NoTradeDecision
  explain: Explainability
}

export interface ScanSettings {
  minEdge: number
  minDataQuality: number
  minConfidence: number
  minLiquidity: number
}

export const DEFAULT_SCAN_SETTINGS: ScanSettings = {
  minEdge: 0.02,
  minDataQuality: 0.35,
  minConfidence: 0.35,
  minLiquidity: 0.25,
}

export interface RankWeights {
  ev: number
  confidence: number
  liquidity: number
  risk: number
}

export const DEFAULT_WEIGHTS: RankWeights = { ev: 0.45, confidence: 0.25, liquidity: 0.15, risk: 0.15 }

export function noTradeRule(input: OpportunityInput, settings: ScanSettings): NoTradeDecision {
  const reasons: string[] = []
  const marketProb = decimalToImplied(input.decimalOdds)
  const edge = input.modelProb - marketProb
  const ev = expectedValue({
    modelProb: input.modelProb,
    decimalOdds: input.decimalOdds,
    feePct: input.feePct,
    spreadPct: input.spreadPct,
    minEdge: settings.minEdge,
  })

  if (ev.evPerUnit <= 0) reasons.push('Expected value is zero or negative after fees and spread.')
  if (edge < settings.minEdge) {
    reasons.push(
      `Edge ${(edge * 100).toFixed(1)}% is below the ${(settings.minEdge * 100).toFixed(1)}% minimum.`,
    )
  }
  if (input.dataQuality < settings.minDataQuality) reasons.push('Data quality is too poor to trust the estimate.')
  if (input.confidence < settings.minConfidence) reasons.push('Confidence is too low to commit capital.')
  if (input.liquidity < settings.minLiquidity) reasons.push('Liquidity is too thin to enter and exit cleanly.')

  return { skip: reasons.length > 0, reasons }
}

export function evaluateOpportunity(
  input: OpportunityInput,
  settings: ScanSettings = DEFAULT_SCAN_SETTINGS,
  weights: RankWeights = DEFAULT_WEIGHTS,
): ScoredOpportunity {
  const marketProb = decimalToImplied(input.decimalOdds)
  const ev = expectedValue({
    modelProb: input.modelProb,
    decimalOdds: input.decimalOdds,
    feePct: input.feePct,
    spreadPct: input.spreadPct,
    minEdge: settings.minEdge,
  })
  const edge = input.modelProb - marketProb
  const noTrade = noTradeRule(input, settings)

  const evScore = clamp(0.5 + ev.evPerUnit * 2, 0, 1)
  const riskPenalty =
    (1 - input.dataQuality) * 0.5 + (1 - input.confidence) * 0.3 + (1 - input.liquidity) * 0.2
  const rawScore =
    weights.ev * evScore +
    weights.confidence * input.confidence +
    weights.liquidity * input.liquidity -
    weights.risk * riskPenalty
  const score = noTrade.skip ? clamp(rawScore, 0, 1) * 0.4 : clamp(rawScore, 0, 1)

  const riskyDespiteEdge =
    ev.evPerUnit > 0 && (input.dataQuality < 0.4 || input.liquidity < 0.3 || input.confidence < 0.4)

  const action: EvDecision = noTrade.skip ? 'skip' : decideAction(edge, ev.evPerUnit, settings.minEdge)

  return {
    input,
    marketProb,
    edge,
    evPerUnit: ev.evPerUnit,
    score,
    action,
    riskyDespiteEdge,
    noTrade,
    explain: explainEdge(input, edge, ev.evPerUnit, riskyDespiteEdge, noTrade),
  }
}

export function rankOpportunities(
  inputs: readonly OpportunityInput[],
  settings: ScanSettings = DEFAULT_SCAN_SETTINGS,
  weights: RankWeights = DEFAULT_WEIGHTS,
): ScoredOpportunity[] {
  return inputs
    .map((i) => evaluateOpportunity(i, settings, weights))
    .sort((a, b) => b.score - a.score)
}

export function explainEdge(
  input: OpportunityInput,
  edge: number,
  evPerUnit: number,
  riskyDespiteEdge: boolean,
  noTrade: NoTradeDecision,
): Explainability {
  const kindData: Record<PositionKind, string[]> = {
    stock: ['OHLCV price history', 'technical indicators (trend, RSI, MACD, ATR)', 'volatility estimate'],
    prediction: ['contract yes/no price', 'implied probability', 'bid/ask spread', 'liquidity score'],
    sportsbook: ['posted odds', 'no-vig fair probability', 'closing-line reference', 'model probability'],
  }

  const verdict: Explainability['verdict'] = noTrade.skip
    ? 'skip'
    : riskyDespiteEdge || evPerUnit < 0.03
      ? 'watchlist-only'
      : 'trade-worthy'

  return {
    dataUsed: kindData[input.kind],
    assumptions: [
      `Your probability estimate (${(input.modelProb * 100).toFixed(1)}%) is accurate and unbiased.`,
      'Fees, spread, and slippage are correctly modeled.',
      'The market price reflects current information.',
      input.notes ? `Manual note: ${input.notes}` : 'No additional qualitative information provided.',
    ],
    whatCouldGoWrong: [
      'Your probability is overconfident and the true edge is smaller or negative.',
      input.liquidity < 0.4 ? 'Thin liquidity could prevent a fair entry or exit.' : 'Price can move against you before fills.',
      'A news/event shock invalidates the historical pattern.',
      riskyDespiteEdge ? 'Positive EV here is fragile — data quality or confidence is weak.' : 'Variance can produce a long losing streak even with real edge.',
    ],
    whyModelMayBeWrong: `Models are simplifications. With data quality ${(input.dataQuality * 100).toFixed(0)}% and confidence ${(input.confidence * 100).toFixed(0)}%, the ${(edge * 100).toFixed(1)}% edge could be sampling noise.`,
    whatInvalidates:
      input.kind === 'stock'
        ? 'A close beyond your invalidation level, a volatility regime change, or an earnings/news surprise.'
        : 'The line moving against you to your model probability, or new information that changes the true probability.',
    verdict,
  }
}
